import {
  ReactElement,
  SyntheticEvent,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import {
  DialogTitle,
  DialogContentText,
  Stack,
  TextField,
  FormControlLabel,
  Switch,
  FormHelperText,
  InputAdornment,
  Button,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useFormik, FormikHelpers } from 'formik';
import { useSnackbar } from 'notistack';

import { useCurrentCapacityTypeInfo, useLazyRequest } from '~hooks/index';
import {
  updateAdmingBillingListBilling,
  createAdmingBillingListBilling,
} from '~services/admin/billingList/billing';
import { AdminBillingListBillingBase } from '~services/admin/billingList/billing/types';
import {
  getDescriptionCapacity,
  getSuffixCapacity,
} from '~utils/capacityTypes';
import { hasError } from '~utils/formHelpers';
import { parseSkillsStringToArray } from '~utils/vehicle';
import { RoutesActionFilterChip } from '~modules/Routes/styles';
import { SelectOption } from '~globals/types';

import { DialogBillingListBillingFormContentProps } from './types';
import CustomAutocomplete from '../CustomAutocomplete';
import { CustomAutocompleteRef } from '../CustomAutocomplete/types';

import {
  FIELDS_NAME,
  getInitialValues,
  validationSchema,
  getBaseBodyRequest,
  getEnabledField,
} from './utils';
import {
  DialogBillingListBillingFormContentContainer,
  DialogBillingListBillingFormActions,
} from './styles';

const DialogBillingListBillingFormContent = ({
  billingListId,
  billingId,
  defaultBillingListCode,
  data,
  serviceTypes,
  onClose,
  onSuccessSubmit,
}: DialogBillingListBillingFormContentProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const currentCapacities = useCurrentCapacityTypeInfo();

  const isUpdate = useMemo(() => Boolean(billingId), [billingId]);

  const title = useMemo(
    () => (isUpdate ? 'Editar tarifa' : 'Agregar tarifa'),
    [isUpdate],
  );

  const submitTile = useMemo(
    () => (isUpdate ? 'Guardar cambios' : 'Agregar'),
    [isUpdate],
  );

  const [, loadingUpdateBilling, , executeUpdateBilling] = useLazyRequest({
    request: updateAdmingBillingListBilling,
    withPostSuccess: (response) => {
      const currentBillingData = response.data
        ?.data as AdminBillingListBillingBase;

      // eslint-disable-next-line max-len
      const message = `La tarifa ${currentBillingData?.billingCode} fue actualizada correctamente`;

      enqueueSnackbar(message, { variant: 'success' });

      onSuccessSubmit?.(currentBillingData);
    },
    withPostFailure: () => {
      const errorMessage = 'Ha ocurrido un error, intente nuevamente';

      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });

  const [, loadingCreateBilling, , executeCreateBilling] = useLazyRequest({
    request: createAdmingBillingListBilling,
    withPostSuccess: (response) => {
      const currentBillingData = response.data
        ?.data as AdminBillingListBillingBase;

      // eslint-disable-next-line max-len
      const message = `La tarifa ${currentBillingData?.billingCode} fue agregada correctamente`;

      enqueueSnackbar(message, { variant: 'success' });

      onSuccessSubmit?.(currentBillingData);
    },
    withPostFailure: () => {
      const errorMessage = 'Ha ocurrido un error, intente nuevamente';

      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });

  const onSubmit = useCallback(
    async (
      values: ReturnType<typeof getInitialValues>,
      { setSubmitting }: FormikHelpers<ReturnType<typeof getInitialValues>>,
    ) => {
      const baseBodyRequest = getBaseBodyRequest(values);

      if (isUpdate && billingId) {
        await executeUpdateBilling({
          billingListId,
          billingId,
          ...baseBodyRequest,
        });
      } else {
        await executeCreateBilling({
          billingListId,
          ...baseBodyRequest,
        });
      }

      setSubmitting(false);
    },
    [
      billingId,
      billingListId,
      executeCreateBilling,
      executeUpdateBilling,
      isUpdate,
    ],
  );

  const loadingSubmit = useMemo(
    () => loadingUpdateBilling || loadingCreateBilling,
    [loadingUpdateBilling, loadingCreateBilling],
  );

  const formikBag = useFormik({
    initialValues: getInitialValues(data),
    validationSchema,
    onSubmit,
  });

  const {
    errors,
    touched,
    getFieldProps,
    submitForm,
    values,
    isSubmitting,
    setFieldValue,
    setFieldTouched,
    dirty,
  } = formikBag;

  const disabledSubmit = useMemo(
    () => (isUpdate ? !dirty : false),
    [dirty, isUpdate],
  );

  const refServiceTypesAutocomplete =
    useRef<CustomAutocompleteRef<SelectOption, true>>(null);

  const setDefaultServicesOptions = useCallback(() => {
    const defaultServicesTypesIds = parseSkillsStringToArray(
      data?.billingByServiceType?.serviceTypes ?? null,
    );

    if (defaultServicesTypesIds.length) {
      const defaultServicesOption = serviceTypes.filter((serviceType) =>
        defaultServicesTypesIds.includes(serviceType.value as string),
      );

      if (defaultServicesOption.length) {
        refServiceTypesAutocomplete.current?.setValue(defaultServicesOption);
      }
    }
  }, [data, serviceTypes]);

  useEffect(() => {
    if (isUpdate) {
      setDefaultServicesOptions();
    }
  }, [isUpdate, setDefaultServicesOptions]);

  const handleChangeServicesTypes = useCallback(
    (_event: SyntheticEvent, options: SelectOption[]) => {
      const servicesTypesIds = options.map((option) => option.value);

      setFieldValue(FIELDS_NAME.SERVICES_TYPES, servicesTypesIds);
    },
    [setFieldValue],
  );

  return (
    <>
      <DialogTitle>{title}</DialogTitle>

      <DialogBillingListBillingFormContentContainer dividers>
        <Stack spacing={2}>
          <DialogContentText>
            Puedes especificar reglas para aplicar la tarifa más conveniente.
            Los rangos e intervalos no deben superponerse
          </DialogContentText>

          <Stack spacing={3} direction="row">
            <TextField
              label="Código de tarifa"
              autoComplete="off"
              fullWidth
              error={hasError(touched, errors, FIELDS_NAME.CODE)}
              {...getFieldProps(FIELDS_NAME.CODE)}
              InputLabelProps={{ shrink: true }}
              InputProps={
                defaultBillingListCode
                  ? {
                      startAdornment: (
                        // eslint-disable-next-line max-len
                        <InputAdornment position="start">{`${defaultBillingListCode}-`}</InputAdornment>
                      ),
                    }
                  : undefined
              }
            />

            <TextField
              label="Tarifa"
              autoComplete="off"
              fullWidth
              type="number"
              error={hasError(touched, errors, FIELDS_NAME.RATE)}
              {...getFieldProps(FIELDS_NAME.RATE)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
          </Stack>

          <Stack spacing={0.5}>
            <FormControlLabel
              control={
                <Switch
                  {...getFieldProps(FIELDS_NAME.ENABLE_RANGE_ZIP_CODES)}
                  checked={values[FIELDS_NAME.ENABLE_RANGE_ZIP_CODES]}
                />
              }
              label="Rango de códigos postales"
              sx={{ maxWidth: 'max-content' }}
            />

            <Stack spacing={0.5}>
              <TextField
                label="Rango de CPs"
                autoComplete="off"
                fullWidth
                error={hasError(touched, errors, FIELDS_NAME.ZIP_CODES)}
                {...getFieldProps(FIELDS_NAME.ZIP_CODES)}
                InputLabelProps={{ shrink: true }}
                disabled={
                  !getEnabledField(values, FIELDS_NAME.ENABLE_RANGE_ZIP_CODES)
                }
              />

              <FormHelperText>
                Puedes especificar uno o varios códigos postales, o definir un
                intervalo. Ej: 1200,1205,1500-1800.
              </FormHelperText>
            </Stack>
          </Stack>

          <Stack spacing={3} direction="row">
            <Stack spacing={0.5} flex={1}>
              <FormControlLabel
                control={
                  <Switch
                    {...getFieldProps(FIELDS_NAME.ENABLE_RANGE_UNIT_1)}
                    checked={values[FIELDS_NAME.ENABLE_RANGE_UNIT_1]}
                  />
                }
                label={`Rango de capacidad ${getDescriptionCapacity(
                  currentCapacities.unit1CapacityTypeId,
                )}`}
                sx={{ maxWidth: 'max-content' }}
              />

              <Stack spacing={0.5} direction="row">
                <TextField
                  label="Desde"
                  autoComplete="off"
                  fullWidth
                  type="number"
                  error={hasError(touched, errors, FIELDS_NAME.UNIT_1_MIN)}
                  {...getFieldProps(FIELDS_NAME.UNIT_1_MIN)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {getSuffixCapacity(
                          currentCapacities.unit1CapacityTypeId,
                        )}
                      </InputAdornment>
                    ),
                  }}
                  disabled={
                    !getEnabledField(values, FIELDS_NAME.ENABLE_RANGE_UNIT_1)
                  }
                />

                <TextField
                  label="Hasta"
                  autoComplete="off"
                  fullWidth
                  type="number"
                  error={hasError(touched, errors, FIELDS_NAME.UNIT_1_MAX)}
                  {...getFieldProps(FIELDS_NAME.UNIT_1_MAX)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {getSuffixCapacity(
                          currentCapacities.unit1CapacityTypeId,
                        )}
                      </InputAdornment>
                    ),
                  }}
                  disabled={
                    !getEnabledField(values, FIELDS_NAME.ENABLE_RANGE_UNIT_1)
                  }
                />
              </Stack>
            </Stack>

            <Stack spacing={0.5} flex={1}>
              <FormControlLabel
                control={
                  <Switch
                    {...getFieldProps(FIELDS_NAME.ENABLE_RANGE_UNIT_2)}
                    checked={values[FIELDS_NAME.ENABLE_RANGE_UNIT_2]}
                  />
                }
                label={`Rango de capacidad ${getDescriptionCapacity(
                  currentCapacities.unit2CapacityTypeId,
                )}`}
                sx={{ maxWidth: 'max-content' }}
              />

              <Stack spacing={0.5} direction="row">
                <TextField
                  label="Desde"
                  autoComplete="off"
                  fullWidth
                  type="number"
                  error={hasError(touched, errors, FIELDS_NAME.UNIT_2_MIN)}
                  {...getFieldProps(FIELDS_NAME.UNIT_2_MIN)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {getSuffixCapacity(
                          currentCapacities.unit2CapacityTypeId,
                        )}
                      </InputAdornment>
                    ),
                  }}
                  disabled={
                    !getEnabledField(values, FIELDS_NAME.ENABLE_RANGE_UNIT_2)
                  }
                />

                <TextField
                  label="Hasta"
                  autoComplete="off"
                  fullWidth
                  type="number"
                  error={hasError(touched, errors, FIELDS_NAME.UNIT_2_MAX)}
                  {...getFieldProps(FIELDS_NAME.UNIT_2_MAX)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {getSuffixCapacity(
                          currentCapacities.unit2CapacityTypeId,
                        )}
                      </InputAdornment>
                    ),
                  }}
                  disabled={
                    !getEnabledField(values, FIELDS_NAME.ENABLE_RANGE_UNIT_2)
                  }
                />
              </Stack>
            </Stack>
          </Stack>

          <Stack spacing={3} direction="row">
            <Stack spacing={0.5} flex={1}>
              <FormControlLabel
                control={
                  <Switch
                    {...getFieldProps(FIELDS_NAME.ENABLE_RANGE_PRICE)}
                    checked={values[FIELDS_NAME.ENABLE_RANGE_PRICE]}
                  />
                }
                label="Rango de precios"
                sx={{ maxWidth: 'max-content' }}
              />

              <Stack spacing={0.5} direction="row">
                <TextField
                  label="Desde"
                  autoComplete="off"
                  fullWidth
                  type="number"
                  error={hasError(touched, errors, FIELDS_NAME.PRICE_MIN)}
                  {...getFieldProps(FIELDS_NAME.PRICE_MIN)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  disabled={
                    !getEnabledField(values, FIELDS_NAME.ENABLE_RANGE_PRICE)
                  }
                />

                <TextField
                  label="Hasta"
                  autoComplete="off"
                  fullWidth
                  type="number"
                  error={hasError(touched, errors, FIELDS_NAME.PRICE_MAX)}
                  {...getFieldProps(FIELDS_NAME.PRICE_MAX)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  disabled={
                    !getEnabledField(values, FIELDS_NAME.ENABLE_RANGE_PRICE)
                  }
                />
              </Stack>
            </Stack>

            <Stack spacing={0.5} flex={1}>
              <FormControlLabel
                control={
                  <Switch
                    {...getFieldProps(FIELDS_NAME.ENABLE_RANGE_SERVICES)}
                    checked={values[FIELDS_NAME.ENABLE_RANGE_SERVICES]}
                  />
                }
                label="Tipos de servicios"
                sx={{ maxWidth: 'max-content' }}
              />

              <Stack spacing={0.5}>
                <CustomAutocomplete
                  innerRef={refServiceTypesAutocomplete}
                  multiple
                  disableCloseOnSelect
                  filterSelectedOptions
                  limitTags={1}
                  renderTags={(vals, getTagsProps) =>
                    vals.map((val, index) => (
                      // eslint-disable-next-line react/jsx-key
                      <RoutesActionFilterChip
                        label={val.label.toString()}
                        size="small"
                        {...getTagsProps({ index })}
                      />
                    ))
                  }
                  disableClearable
                  options={serviceTypes}
                  label="Servicios"
                  name="services"
                  onChange={handleChangeServicesTypes}
                  onBlur={() =>
                    setFieldTouched(FIELDS_NAME.SERVICES_TYPES, true)
                  }
                  fullWidth
                  getOptionLabel={(option) => option.label.toString()}
                  error={hasError(touched, errors, FIELDS_NAME.SERVICES_TYPES)}
                  InputLabelProps={{ shrink: true }}
                  disabled={
                    !getEnabledField(values, FIELDS_NAME.ENABLE_RANGE_SERVICES)
                  }
                />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </DialogBillingListBillingFormContentContainer>

      <DialogBillingListBillingFormActions>
        <Button color="secondary" onClick={onClose}>
          Cerrar
        </Button>

        <LoadingButton
          variant="contained"
          color="primary"
          onClick={submitForm}
          loading={loadingSubmit || isSubmitting}
          disabled={disabledSubmit}
        >
          {submitTile}
        </LoadingButton>
      </DialogBillingListBillingFormActions>
    </>
  );
};

export default DialogBillingListBillingFormContent;
